/* System defined core library */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { BootstrapParams } from '../../../../../bootstrap-params';
import { SchedulingApiService } from '../../services/scheduling-api.service';
import { SelectedExamDetailService } from '../../services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../../shared/models/scheduling/appointmentSlot.model';
import { Client } from '../../../../shared/models/scheduling/client.model';
import { Exam } from '../../../../shared/models/scheduling/exam.model';
import { Payment } from '../../../../shared/models/scheduling/payment.model';
import { Program } from '../../../../shared/models/scheduling/program.model';
import { SiteSlot } from '../../../../shared/models/scheduling/siteSlot.model';

/* User defined enums/constants */
import { Fee } from '../../../../shared/enums/scheduling.enum';


@Component({
  selector: 'app-exam-context',
  templateUrl: './exam-context.component.html'
})
export class ExamContextComponent implements OnInit {
  /* User defined private/public variables */
  public exams: Exam[];
  public isExamSets: boolean;
  public selectedAppointments: AppointmentDetail[] = [];
  public hiddenClient: boolean;

  /* Input-OutPut parameters decalartions */
  @Input() public selectedClient: Client;
  @Input() public selectedProgram: Program;
  @Input() public selectedExam: Exam;
  @Output() public canProceedFwd: EventEmitter<boolean> = new EventEmitter();
  @Output() public examDuration: EventEmitter<number> = new EventEmitter();

  /* Service subscriptions */
  selectedAppointments$: Subscription;
  translateLanguage$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private bootstrapParams: BootstrapParams,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private translate: TranslateService
  ) {
    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.hiddenClient = bootstrapParams.hiddenClient;
  }

  /* Initialize the component & call require methods when component loaded first time. */
  ngOnInit() {
    if (!!this.selectedProgram) {
      this.getExams();
    }
    this.canProceedFwd.emit(!!this.selectedAppointments && !!this.selectedAppointments.length);
  }

  /* Set Selected Client */
  setSelectedClient(client: Client): void {
    this.selectedClient = client;
    this.selectedExamDetailService.clientCode = !!client && !!client.code ? client.code : '';
    this.selectedProgram = null;
    this.clearAppointmentDetails();
    this.selectedExam = null;
  }

  /* Set Selected Program */
  setSelectedProgram(prgram: Program): void {
    this.selectedProgram = prgram;
    this.selectedExam = null;
    this.clearAppointmentDetails();
    this.getExams();
  }

  getExams() {
    this.exams = [];
    this.schedulingApiService.getExams(this.selectedProgram.id).subscribe((exams) => {
      // single exams
      if (exams.every(exam => !exam.examSet.id)) {
        this.isExamSets = false;
        this.exams = exams;
      }
      // exam set(s)
      else {
        this.isExamSets = true;
        this.exams = exams.filter(exam => !exam.examSet.id || !!exam.examSet.attributes.b2b);
        this.exams.forEach(exam => {
          if (!!exam.examSet && exam.examSet.attributes) {
            // User story #244635 Insert New Component into Find Availability Flow: All sets should be treated as schedule not required.
            exam.examSet.attributes.requiredSchedule = false;
          }
        });
      }

      // default: select exams if appointments are selected
      if (this.selectedAppointments && this.selectedAppointments.length) {
        this.exams.forEach((exam: Exam) => {
          if (this.selectedAppointments.filter(appt => appt.exam.code === exam.code).length) {
            exam.isSelected = true;
          }
        });
      }
    });
  }

  duration($event: number) {
    this.examDuration.emit($event);
  }

  setSelectedExam(exam: Exam): void {
    if (!!exam && exam.status !== 'invalid ScheduleMethod') {
      this.selectedExam = exam;
      this.selectedExamDetailService.setExam(this.selectedExam);
      this.selectedExamDetailService.setCountry(null);
      this.selectedExamDetailService.setRegion(null);
      this.selectedExamDetailService.setState(null);

      this.setAppointmentDetails([exam]);
      this.canProceedFwd.emit(!!this.selectedAppointments && !!this.selectedAppointments.length);
    } else {
      this.clearAppointmentDetails();
    }
  }

  clearAppointmentDetails(): void {
    this.selectedAppointments = [];
    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
    this.canProceedFwd.emit(!!this.selectedAppointments && !!this.selectedAppointments.length);
  }

  setAppointmentDetails(exams: Exam[]) {
    exams.forEach((exam) => {
      let tempAppointmentDetail: AppointmentDetail = new AppointmentDetail();
      tempAppointmentDetail.exam = exam;
      tempAppointmentDetail.program = this.selectedProgram;
      tempAppointmentDetail.examName = exam.name;
      tempAppointmentDetail.client = this.selectedClient;
      tempAppointmentDetail.appointmentSlot = new AppointmentSlot();
      tempAppointmentDetail.appointmentSlot.site = new SiteSlot(null, null, null, null, null, null);
      this.selectedAppointments = [];
      tempAppointmentDetail.payments = [];
      let payment: Payment = new Payment(0.0, null, Fee.ExamFee);
      tempAppointmentDetail.payments.push(payment);
      this.selectedAppointments.push(tempAppointmentDetail);
      this.selectedExamDetailService.selectedAppointmentIndex = 0;
      this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
      this.selectedExamDetailService.appointmentCountForInfo.next(0);
      this.selectedExamDetailService.setFormInputData(null);
    });
  }

  canProceed($event: boolean) {
    this.canProceedFwd.emit($event);
  }
}
